/**
 * Per-sport rules driving every UI decision in the Match List block.
 * Adding a sport: append one entry here and one score component in
 * ../frontend/scores/scoreRegistry.ts.
 */
import type { FUSportsLivescoreSport } from "fansunited-data-layer";

export type Sport = "football" | "basketball" | "tennis" | "hockey" | "american_football";

export type MatchListMode =
    | "entityUpcoming"
    | "entityRecent"
    | "singleRound"
    | "competitionDateRange"
    | "multiCompetitionDate"
    | "manual";

export type ScoreRendererKey = "football" | "basketball" | "tennis" | "hockey" | "american_football";

/**
 * Which localized `matches.<key>` label to use as the round prefix.
 * `null` means "no prefix — use the stage name as-is" (tennis draw rounds like "Quarter-Final").
 */
export type RoundLabelKey = "matchday" | "round" | "week" | null;

export interface RoundLabelInput {
    matchday: number;
    stageName?: string | null;
}

/** Localized round labels, sourced from `labels.matches.<key>` at render time. */
export interface RoundLabelStrings {
    matchday: string;
    round: string;
    week: string;
}

/**
 * Format a round label per the sport's rules using localized strings.
 * Callers pass in the resolved label strings for the current locale.
 *
 * Returns "" (empty) when no label can be built — caller decides fallback
 * (e.g. hide the label entirely, or show the raw round id).
 */
export function formatRoundLabel(
    key: RoundLabelKey,
    input: RoundLabelInput,
    labels: RoundLabelStrings
): string {
    if (key === null) return input.stageName?.trim() || "";
    if (input.matchday && input.matchday > 0) return `${labels[key]} ${input.matchday}`;
    return input.stageName?.trim() || "";
}

export interface MarketOption {
    label: string;
    value: string;
}

export interface SportRules {
    entityKind: "team" | "athlete";
    entityIdPrefix: string;
    competitionIdPrefix: string;
    sportApiEnum: FUSportsLivescoreSport;
    modes: MatchListMode[];
    defaultMode: MatchListMode;
    markets: MarketOption[];
    defaultMarket: string | null;
    /** Which `matches.<key>` label to use when rendering a round for this sport. */
    roundLabelKey: RoundLabelKey;
    scoreRenderer: ScoreRendererKey;
}

const ALL_MODES: MatchListMode[] = [
    "entityUpcoming",
    "entityRecent",
    "singleRound",
    "competitionDateRange",
    "multiCompetitionDate",
    "manual",
];

const TENNIS_MODES: MatchListMode[] = [
    "entityUpcoming",
    "entityRecent",
    "singleRound",
    "competitionDateRange",
    "manual",
];

const FOOTBALL_MARKETS: MarketOption[] = [
    { label: "1X2", value: "FT_1X2" },
    { label: "Double Chance", value: "DOUBLE_CHANCE" },
    { label: "Both Teams to Score", value: "BOTH_TEAMS_SCORE" },
    { label: "Over/Under 2.5 Goals", value: "OVER_GOALS_2_5" },
    { label: "Half Time / Full Time", value: "HT_FT" },
];

export const SPORT_RULES: Record<Sport, SportRules> = {
    football: {
        entityKind: "team",
        entityIdPrefix: "fb:t:",
        competitionIdPrefix: "fb:c:",
        sportApiEnum: "FOOTBALL",
        modes: ALL_MODES,
        defaultMode: "entityUpcoming",
        markets: FOOTBALL_MARKETS,
        defaultMarket: "FT_1X2",
        roundLabelKey: "matchday",
        scoreRenderer: "football",
    },
    basketball: {
        entityKind: "team",
        entityIdPrefix: "bb:t:",
        competitionIdPrefix: "bb:c:",
        sportApiEnum: "BASKETBALL",
        modes: ALL_MODES,
        defaultMode: "entityUpcoming",
        markets: [],
        defaultMarket: null,
        roundLabelKey: "round",
        scoreRenderer: "basketball",
    },
    tennis: {
        entityKind: "athlete",
        entityIdPrefix: "tn:p:",
        competitionIdPrefix: "tn:c:",
        sportApiEnum: "TENNIS",
        modes: TENNIS_MODES,
        defaultMode: "entityUpcoming",
        markets: [],
        defaultMarket: null,
        roundLabelKey: null,
        scoreRenderer: "tennis",
    },
    hockey: {
        entityKind: "team",
        entityIdPrefix: "hk:t:",
        competitionIdPrefix: "hk:c:",
        sportApiEnum: "HOCKEY",
        modes: ALL_MODES,
        defaultMode: "entityUpcoming",
        markets: [],
        defaultMarket: null,
        roundLabelKey: "week",
        scoreRenderer: "hockey",
    },
    american_football: {
        entityKind: "team",
        entityIdPrefix: "af:t:",
        competitionIdPrefix: "af:c:",
        sportApiEnum: "AMERICAN_FOOTBALL",
        modes: ALL_MODES,
        defaultMode: "entityUpcoming",
        markets: [],
        defaultMarket: null,
        roundLabelKey: "week",
        scoreRenderer: "american_football",
    },
};

export const SPORTS: Sport[] = ["football", "basketball", "tennis", "hockey", "american_football"];

export const SPORT_LABELS: Record<Sport, string> = {
    football: "Football",
    basketball: "Basketball",
    tennis: "Tennis",
    hockey: "Hockey",
    american_football: "American Football",
};

export const MATCH_LIST_MODES: { value: MatchListMode; label: string }[] = [
    { value: "entityUpcoming", label: "Team / Player upcoming" },
    { value: "entityRecent", label: "Team / Player recent" },
    { value: "singleRound", label: "Single round of a competition" },
    { value: "competitionDateRange", label: "Competition on date range" },
    { value: "multiCompetitionDate", label: "Multiple competitions on date range" },
    { value: "manual", label: "Manual selection" },
];

export function isSport(v: unknown): v is Sport {
    return typeof v === "string" && (SPORTS as string[]).includes(v);
}

export function isMatchListMode(v: unknown): v is MatchListMode {
    return typeof v === "string" && MATCH_LIST_MODES.some((m) => m.value === v);
}

/**
 * Canvas sport enum → livescore-UI sport slug (dash-separated, lowercase).
 * Single source of truth used by the frontend renderer and the odds-market
 * lookup in the server fetcher.
 */
export const SPORT_TO_LIVESCORE_SLUG: Record<Sport, "football" | "basketball" | "tennis" | "hockey" | "american-football"> = {
    football: "football",
    basketball: "basketball",
    tennis: "tennis",
    hockey: "hockey",
    american_football: "american-football",
};
